import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { ExpandLess, ExpandMore } from "@mui/icons-material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CancelPresentationIcon from "@mui/icons-material/CancelPresentation";
import ChecklistIcon from "@mui/icons-material/Checklist";
import ContactPhoneIcon from "@mui/icons-material/ContactPhone";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import DashboardIcon from "@mui/icons-material/Dashboard";
import EventIcon from "@mui/icons-material/Event";
import LightModeIcon from "@mui/icons-material/LightMode";
import LogoutIcon from "@mui/icons-material/Logout";
import PeopleIcon from "@mui/icons-material/People";
import PersonIcon from "@mui/icons-material/Person";
import SettingsIcon from "@mui/icons-material/Settings";
import StoreIcon from "@mui/icons-material/Store";
import WorkIcon from "@mui/icons-material/Work";
import {
  Avatar,
  Box,
  Button,
  Collapse,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";

import EZlogoTransparent from "../assets/EZpresta-logo-trans.png";
import { useAuth } from "../contexts/AuthContext";
import LanguageSwitcher from "./LanguageSwitcher";

const Sidebar = ({ mobileOpen, handleDrawerToggle, darkMode, toggleTheme }) => {
  const { currentUser, logout } = useAuth();
  const [openSettings, setOpenSettings] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const handleSettingsClick = () => {
    setOpenSettings(!openSettings);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch {
      // no-op
    }
  };

  const menuItems = [
    {
      text: t("navigation.dashboard"),
      icon: <DashboardIcon />,
      path: "/dashboard"
    },
    { text: t("navigation.catalog"), icon: <StoreIcon />, path: "/catalogue" },
    { text: t("navigation.clients"), icon: <PeopleIcon />, path: "/customers" },
    { text: t("navigation.agenda"), icon: <EventIcon />, path: "/agenda" },
    { text: "Todo", icon: <ChecklistIcon />, path: "/todo" },
    {
      text: t("navigation.newAppointment"),
      icon: <AddCircleIcon />,
      path: "/new-appointment"
    }
  ];

  const isPathActive = path => location.pathname === path;
  const isSettingsActive = location.pathname.startsWith("/settings")
    || location.pathname.startsWith("/customer-source")
    || location.pathname.startsWith("/rejection-reasons")
    || location.pathname.startsWith("/workflow");

  const drawer = (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column"
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          px: 2,
          pt: 3,
          pb: 2,
          textAlign: "center"
        }}
      >
        {currentUser && currentUser.logo ? (
          <Avatar
            src={currentUser.logo}
            alt={currentUser.username || currentUser.email}
            sx={{ width: 88, height: 88, mb: 1.25 }}
          />
        ) : (
          <Avatar sx={{ width: 88, height: 88, mb: 1.25 }} />
        )}
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {currentUser?.name || currentUser?.username || currentUser?.email || "Utilisateur"}
        </Typography>
      </Box>

      <Divider />

      <Box sx={{ flexGrow: 1, overflow: "auto", py: 1 }}>
        <List
          sx={{
            "& .MuiListItemText-primary": {
              fontSize: "1rem"
            }
          }}
        >
          {menuItems.map(item => (
            <ListItem
              key={item.text}
              disablePadding
              sx={{
                color: theme =>
                  theme.palette.mode === "dark" ? "white" : "#475569",
                "& .MuiListItemIcon-root": {
                  color: theme =>
                    theme.palette.mode === "dark" ? "white" : "#64748b"
                },
                ...(isPathActive(item.path) && {
                  backgroundColor: "rgba(212, 164, 17, 0.12)",
                  color: "#b98908",
                  borderRight: "3px solid #d4a411",
                  borderRadius: "0 10px 10px 0",
                  mr: 1.5,
                  "& .MuiListItemIcon-root": {
                    color: "#b98908"
                  }
                }),
                "&:hover": {
                  backgroundColor: theme =>
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.08)"
                      : "#f1f5f9",
                  "& .MuiListItemIcon-root": {
                    color: "#b98908"
                  }
                }
              }}
            >
              <ListItemButton
                component={Link}
                to={item.path}
                sx={{
                  borderRadius: "0 10px 10px 0"
                }}
                onClick={() => isMobile && handleDrawerToggle()}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            </ListItem>
          ))}

          <Box sx={{ px: 2, pt: 2.5, pb: 1 }}>
            <Button
              component={Link}
              to="/reservation"
              fullWidth
              startIcon={<AddCircleOutlineIcon />}
              onClick={() => isMobile && handleDrawerToggle()}
              sx={{
                py: 1.4,
                borderRadius: 2.5,
                textTransform: "none",
                fontWeight: 700,
                fontSize: "1rem",
                backgroundColor: "#d4a411",
                color: "#221d10",
                boxShadow: "0 10px 24px rgba(212, 164, 17, 0.22)",
                "&:hover": {
                  backgroundColor: "#c2960f",
                  boxShadow: "0 10px 24px rgba(212, 164, 17, 0.28)"
                }
              }}
            >
              {t("navigation.newReservation")}
            </Button>
          </Box>

        </List>
      </Box>

      <Divider />

      <Box sx={{ p: 2, pt: 1.5 }}>
        <List
          sx={{
            p: 0,
            mb: 1,
            "& .MuiListItemText-primary": {
              fontSize: "1rem"
            }
          }}
        >
          <ListItem disablePadding>
            <ListItemButton
              onClick={handleSettingsClick}
              sx={{
                color: theme => theme.palette.mode === "dark" ? "white" : "#475569",
                borderRadius: 2,
                "& .MuiListItemIcon-root": {
                  color: theme => theme.palette.mode === "dark" ? "white" : "#64748b"
                },
                ...(isSettingsActive && {
                  backgroundColor: "rgba(212, 164, 17, 0.12)",
                  color: "#b98908",
                  "& .MuiListItemIcon-root": {
                    color: "#b98908"
                  }
                })
              }}
            >
              <ListItemIcon>
                <SettingsIcon />
              </ListItemIcon>
              <ListItemText primary={t("navigation.settings")} />
              {openSettings ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
          </ListItem>

          <Collapse in={openSettings} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              <ListItem disablePadding>
                <ListItemButton
                  component={Link}
                  to="/settings"
                  sx={{ pl: 4, borderRadius: 2 }}
                  onClick={() => isMobile && handleDrawerToggle()}
                >
                  <ListItemIcon>
                    <PersonIcon />
                  </ListItemIcon>
                  <ListItemText primary={t("navigation.settings")} />
                </ListItemButton>
              </ListItem>

              <ListItem disablePadding>
                <ListItemButton
                  component={Link}
                  to="/customer-source"
                  sx={{ pl: 4, borderRadius: 2 }}
                  onClick={() => isMobile && handleDrawerToggle()}
                >
                  <ListItemIcon>
                    <ContactPhoneIcon />
                  </ListItemIcon>
                  <ListItemText primary={t("navigation.customerSource")} />
                </ListItemButton>
              </ListItem>

              <ListItem disablePadding>
                <ListItemButton
                  component={Link}
                  to="/rejection-reasons"
                  sx={{ pl: 4, borderRadius: 2 }}
                  onClick={() => isMobile && handleDrawerToggle()}
                >
                  <ListItemIcon>
                    <CancelPresentationIcon />
                  </ListItemIcon>
                  <ListItemText primary={t("navigation.rejectionReasons")} />
                </ListItemButton>
              </ListItem>

              <ListItem disablePadding>
                <ListItemButton
                  component={Link}
                  to="/workflow"
                  sx={{ pl: 4, borderRadius: 2 }}
                  onClick={() => isMobile && handleDrawerToggle()}
                >
                  <ListItemIcon>
                    <WorkIcon />
                  </ListItemIcon>
                  <ListItemText primary={t("navigation.workflow")} />
                </ListItemButton>
              </ListItem>

              <ListItem disablePadding>
                <ListItemButton onClick={toggleTheme} sx={{ pl: 4, borderRadius: 2 }}>
                  <ListItemIcon>
                    {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      darkMode
                        ? t("navigation.lightMode")
                        : t("navigation.darkMode")
                    }
                  />
                </ListItemButton>
              </ListItem>

              <ListItem disablePadding>
                <Box sx={{ width: "100%" }}>
                  <LanguageSwitcher
                    showLabel
                    fullWidth
                    sx={{
                      justifyContent: "flex-start",
                      borderRadius: 2,
                      fontWeight: 400,
                      px: 4,
                      py: 0.75,
                      minHeight: 48,
                      "& .MuiButton-startIcon": {
                        minWidth: 40,
                        marginRight: 2,
                        marginLeft: 0
                      }
                    }}
                  />
                </Box>
              </ListItem>
            </List>
          </Collapse>
        </List>

        <Divider sx={{ my: 1.25 }} />

        <Button
          startIcon={<LogoutIcon />}
          color="inherit"
          onClick={handleLogout}
          fullWidth
          sx={{
            textTransform: "none",
            fontWeight: 500,
            justifyContent: "flex-start",
            fontSize: "1rem",
            px: 2,
            py: 0.75,
            minHeight: 48,
            "& .MuiButton-startIcon": {
              minWidth: 40,
              marginRight: 2,
              marginLeft: 0
            }
          }}
        >
          {t("navigation.logout")}
        </Button>

        <Divider sx={{ mt: 1.25, mb: 1.5 }} />

        <Box sx={{ display: "flex", justifyContent: "center", pt: 1 }}>
          <img
            src={EZlogoTransparent}
            alt="EzPresta Logo"
            style={{ width: "100%", maxWidth: 160, opacity: 0.9 }}
          />
        </Box>
      </Box>
    </Box>
  );

  return (
    <Drawer
      variant={isMobile ? "temporary" : "permanent"}
      open={isMobile ? mobileOpen : true}
      onClose={handleDrawerToggle}
      ModalProps={{
        keepMounted: true
      }}
      sx={{
        width: 260,
        flexShrink: 0,
        ["& .MuiDrawer-paper"]: {
          width: 260,
          boxSizing: "border-box",
          borderRight: "1px solid #e2e8f0",
          backgroundColor: theme =>
            theme.palette.mode === "dark" ? "rgb(18, 18, 18)" : "#ffffff",
          color: theme =>
            theme.palette.mode === "dark" ? "#fff" : "inherit"
        }
      }}
    >
      {drawer}
    </Drawer>
  );
};

export default Sidebar;
